import {
  Alert,
  colors,
  FileDisplaySection,
  FileDropZone,
  FileMimeType,
  FileWithUploadProgress,
  Icon,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
} from '@ui-components/neo.tax';
import { useEffect, useMemo, useState } from 'react';

import { ExcelLogo, interpretExcelParseError, ScrollableUnorderedList, validateFile } from '@neo.tax/shared';

import { Modal, StyledActionButton } from './bulk-edit-asc-wage-modal.styled';
import { useGetAscWageReport, useUploadAscWageReport, UploadAscWageReportStatus } from './hooks';
import { InstructionList } from './upload-instructions.styled';

type BulkEditAscWageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  ascWagesBulkEditAnalyticIds: {
    ascWagesExportBulkBtnId: string;
    ascWagesUploadBulkInputId: string;
    ascWagesUploadBulkBtnId: string;
    ascWagesCancelBulkBtnId: string;
  };
};

export const BulkEditAscWageModal = ({
  ascWagesBulkEditAnalyticIds,
  isOpen,
  onClose,
}: BulkEditAscWageModalProps): React.ReactElement => {
  const { ascWagesCancelBulkBtnId, ascWagesExportBulkBtnId, ascWagesUploadBulkBtnId, ascWagesUploadBulkInputId } =
    ascWagesBulkEditAnalyticIds;
  const [file, setFile] = useState<File>();
  const [fileUploads, setFileUploads] = useState<FileWithUploadProgress[]>([]);

  const { errors, reset, status, upload } = useUploadAscWageReport(file);
  const errorMessages = errors?.map((error) => interpretExcelParseError(error));
  const { download, loading } = useGetAscWageReport();

  const onDownloadTemplateClicked = async (): Promise<void> => {
    if (!loading) {
      await download();
    }
  };

  // clear error messages if the uploaded file is changed
  useEffect(() => {
    if (!file) {
      reset();
    }
  }, [file, reset]);

  const onCancel = (): void => {
    reset();
    setFile(undefined);
    onClose();
  };

  useEffect(() => {
    const validateAndSetFileUploads = async (): Promise<void> => {
      if (!file) {
        setFileUploads([]);
        return;
      }

      const { errorMessage, status } = await validateFile(file);
      setFileUploads([
        {
          fileName: file.name,
          uploadPercent: null,
          status,
          errorMessage,
        },
      ]);
    };

    validateAndSetFileUploads();
  }, [file]);
  const hasFileErrors = useMemo(() => fileUploads.some((file) => file.status === 'error'), [fileUploads]);

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose}>
      <Stack direction="column" gap={4}>
        <ModalHeader
          icon={<Icon node={<ExcelLogo />} size={12} variant="none" />}
          iconBgColor={colors.white}
          title="Bulk Edit ASC Cost and Project Allocations"
        />
        {status !== UploadAscWageReportStatus.SUCCESS && (
          <>
            <Stack direction="column" gap={2}>
              <Typography as="p" fontSize="sm" fontWeight="normal" color={colors.gray[500]}>
                Edit the project allocations for ASC Cost in bulk.
              </Typography>
              <InstructionList>
                <Typography as="li" color={colors.gray[500]} fontSize="sm">
                  Download the current allocations{' '}
                  <Typography
                    as="button"
                    fontSize="sm"
                    color={colors.primary[600]}
                    fontWeight="medium"
                    onClick={onDownloadTemplateClicked}
                    id={ascWagesExportBulkBtnId}
                  >
                    here
                  </Typography>
                  .
                </Typography>
                <Typography as="li" color={colors.gray[500]} fontSize="sm">
                  Set or edit allocations in the spreadsheet.
                </Typography>
                <Typography as="li" color={colors.gray[500]} fontSize="sm">
                  Drag and drop the updated spreadsheet below.
                </Typography>
              </InstructionList>
            </Stack>
            <FileDropZone
              allowedMimeTypes={[FileMimeType.EXCEL]}
              addFiles={(files): void => setFile(files[0])}
              text="Your Report"
              allowMultipleSelect={false}
              inputId={ascWagesUploadBulkInputId}
            />
            <FileDisplaySection
              files={fileUploads}
              disable={status === UploadAscWageReportStatus.UPLOADING}
              removeFile={(): void => setFile(undefined)}
            />
          </>
        )}
        {status === UploadAscWageReportStatus.SUCCESS && <Alert title="Upload Success" severity="success" />}
        {status === UploadAscWageReportStatus.ERROR && (
          <Alert
            title="There were errors with your upload"
            subtitle={
              errorMessages ? (
                <ScrollableUnorderedList maxHeight={310}>
                  {errorMessages.map((errorMessage, index) => (
                    <li key={index}>{errorMessage}</li>
                  ))}
                </ScrollableUnorderedList>
              ) : (
                <p>
                  Unfortunately, we ran into an unknown error. Please review the uploaded file again or contact us for
                  help.
                </p>
              )
            }
            severity="error"
          />
        )}
      </Stack>
      <ModalActions hasTopBorder={false}>
        <StyledActionButton
          variant="white"
          rounded="regular"
          size="base"
          onClick={onCancel}
          id={ascWagesCancelBulkBtnId}
          disabled={status === UploadAscWageReportStatus.UPLOADING}
        >
          Cancel
        </StyledActionButton>
        {status === UploadAscWageReportStatus.SUCCESS ? (
          <StyledActionButton variant="primary" rounded="regular" size="base" onClick={onCancel} disabled={!file}>
            Close
          </StyledActionButton>
        ) : (
          <StyledActionButton
            variant="primary"
            rounded="regular"
            size="base"
            onClick={upload}
            id={ascWagesUploadBulkBtnId}
            disabled={!file || status === UploadAscWageReportStatus.UPLOADING || hasFileErrors}
          >
            {status === UploadAscWageReportStatus.UPLOADING ? 'Uploading...' : 'Upload'}
          </StyledActionButton>
        )}
      </ModalActions>
    </Modal>
  );
};
